'use client';

import { AssetPath, SCENE_NAME_EXT } from '@gnsx/genesys.js';
import { useState } from 'react';

import type { NewSceneOptions } from '../components/layout/SaveSceneDialog';

import { saveScene } from '@/app/editor/core/sceneHandler';
import { DEFAULT_FOLDER_PATH } from '@/app/editor/store';
import { editorStore } from '@/app/editor/store/editorStore';
import { PROJECT_ASSETS_FOLDER } from '@/const';
import { showAssetInAssetBrowser } from '@/editor/core/actions/assetBrowserReveal';


/**
 * Custom hook for handling new scene creation
 * Provides reusable logic for creating new scenes with proper state management
 */
export const useNewScene = () => {
  const [isNewSceneDialogOpen, setIsNewSceneDialogOpen] = useState(false);
  const [isSaving, setIsSaving] = useState(false);

  /** Open the new scene dialog */
  const handleNewScene = async () => {
    setIsNewSceneDialogOpen(true);
  };

  /** Handle the actual scene creation and saving */
  const handleConfirmNewScene = async (newSceneName: string, options?: NewSceneOptions) => {
    try {
      editorStore.project.setCurrentScene(null, null);
      await editorStore.scene.newScene();
      await new Promise(resolve => setTimeout(resolve, 1000));
      await handleSaveSceneWithName(newSceneName, options);
    } catch (error) {
      console.error('Failed to create new scene:', error);
    }
  };

  /** Save scene with the provided name */
  const handleSaveSceneWithName = async (newSceneName: string, options?: NewSceneOptions) => {
    // Build scene path in @project/assets/ format for save and default-scene settings.
    // Use asset browser's current folder when it is under project assets; otherwise default to assets/scenes.
    const fileName = newSceneName.endsWith(SCENE_NAME_EXT) ? newSceneName : newSceneName + SCENE_NAME_EXT;
    const currentDir = editorStore.assets.currentProjectAssetDirectory;
    const parentFolder =
      currentDir &&
        (currentDir === PROJECT_ASSETS_FOLDER || currentDir.startsWith(`${PROJECT_ASSETS_FOLDER}/`))
        ? currentDir
        : DEFAULT_FOLDER_PATH.scenes;
    const scenePath = AssetPath.join(parentFolder, fileName);

    // For saveScene, we need the path without @project prefix
    setIsSaving(true);
    try {
      const savedScenePath = await saveScene(scenePath ? AssetPath.fromString(scenePath) : undefined);
      await editorStore.assets.assetTree.refreshDirectory(parentFolder);
      showAssetInAssetBrowser(savedScenePath || scenePath, { selectIfFound: true });

      // Apply default scene settings if options were provided
      if (options) {
        if (options.setAsDefaultGame) {
          editorStore.setDefaultScene(scenePath, 'game');
        }
        if (options.setAsDefaultServer) {
          editorStore.setDefaultScene(scenePath, 'server');
        }
      }
    } catch (error) {
      console.error('Failed to save scene:', error);
    } finally {
      setIsSaving(false);
    }
  };

  return {
    isNewSceneDialogOpen,
    setIsNewSceneDialogOpen,
    isSaving,
    handleNewScene,
    handleConfirmNewScene,
  };
};
